import React, { useState, useEffect, useCallback } from 'react';
import { orgVisibilityAPI } from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { 
  Building2,
  Users,
  Bot,
  DollarSign,
  Activity,
  Shield,
  RefreshCw,
  Loader2,
  Eye,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronRight,
  Wallet,
  TrendingUp,
  Power,
  PauseCircle
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Alert,
  AlertDescription,
} from '../components/ui/alert';

/**
 * OrgOverview - Organization-Level Visibility Dashboard
 * 
 * Phase G Step 7: Org admins can view all tenants under their org.
 * 
 * Features:
 * - View all tenants under org
 * - View AI status per tenant
 * - View budget usage per tenant
 * 
 * CANNOT:
 * - Override tenant execution
 * - Force enable AI
 * - Bypass safety rules
 */
export default function OrgOverview() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetails, setTenantDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [accessError, setAccessError] = useState(null);

  // Fetch org data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, tenantsRes] = await Promise.all([
        orgVisibilityAPI.getOrgOverview(),
        orgVisibilityAPI.getTenants({ include_demo: true })
      ]);
      setOverview(overviewRes.data);
      setTenants(tenantsRes.data.tenants || []);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching org data:', error);
      if (error.response?.status === 403) {
        setAccessError('You do not have permission to view organization data. This page is only available to Organization Admins.');
        toast.error('Access denied');
      } else {
        toast.error('Failed to load organization overview');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch tenant details
  const fetchTenantDetails = async (tenantId) => {
    setDetailsLoading(true);
    try {
      const [aiStatusRes, budgetRes] = await Promise.all([
        orgVisibilityAPI.getTenantAIStatus(tenantId),
        orgVisibilityAPI.getTenantBudget(tenantId)
      ]);
      setTenantDetails({
        aiStatus: aiStatusRes.data,
        budget: budgetRes.data
      });
    } catch (error) {
      console.error('Error fetching tenant details:', error);
      toast.error('Failed to load tenant details');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Open tenant details dialog
  const openTenantDetails = (tenant) => {
    setSelectedTenant(tenant);
    setShowDetailsDialog(true);
    fetchTenantDetails(tenant.tenant_id);
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6" data-testid="org-overview-loading">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-muted animate-pulse" />
          <div className="space-y-2">
            <div className="h-7 w-56 bg-muted rounded animate-pulse" />
            <div className="h-4 w-72 bg-muted/60 rounded animate-pulse" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="p-5 rounded-lg border space-y-2">
              <div className="h-4 w-24 bg-muted/60 rounded animate-pulse" />
              <div className="h-8 w-16 bg-muted rounded animate-pulse" />
            </div>
          ))}
        </div>
        <div className="rounded-lg border p-6 space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-3 border rounded-lg">
              <div className="h-10 w-10 rounded-full bg-muted animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-40 bg-muted rounded animate-pulse" />
                <div className="h-3 w-56 bg-muted/40 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (accessError) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="org-overview-access-denied">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                <Shield className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <CardTitle className="text-red-600">Access Denied</CardTitle>
                <CardDescription>{accessError}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => window.history.back()}>
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="org-overview">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-indigo-600/20 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Organization Overview</h1>
              <p className="text-gray-500 dark:text-gray-400">{overview?.org_name || 'Organization'} - Tenant visibility dashboard</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="border-blue-500/50 text-blue-600 dark:text-blue-400">
            <Eye className="h-3 w-3 mr-1" />
            View Only
          </Badge>
          <Button variant="outline" onClick={fetchData} size="sm" data-testid="refresh-btn">
            <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Safety Notice */}
      <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-900/20">
        <Shield className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-700 dark:text-amber-300">
          This is a read-only view. You cannot override tenant AI execution, force enable AI, or bypass safety rules.
        </AlertDescription>
      </Alert>

      {/* Overview Metrics */}
      <div className="grid grid-cols-4 gap-4" data-testid="org-metrics">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Total Tenants
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold" data-testid="total-tenants">
                {overview?.total_tenants || 0}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {overview?.active_tenants || 0} active, {overview?.demo_tenants || 0} demo
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Wallet className="h-4 w-4" />
              Total Budget
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-green-600" data-testid="total-budget">
                ${overview?.total_budget_usd?.toFixed(2) || '0.00'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              ${overview?.total_used_usd?.toFixed(2) || '0.00'} used
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              AI Runs (30d)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-blue-600" data-testid="ai-runs">
                {overview?.total_ai_runs_30d || 0}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Across all tenants
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Kill Switches
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className={cn(
                "text-3xl font-bold",
                overview?.tenants_with_kill_switch > 0 ? "text-red-600" : "text-green-600"
              )} data-testid="kill-switches">
                {overview?.tenants_with_kill_switch || 0}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Tenants with active kill switch
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tenants List */}
      <Card data-testid="tenants-list">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-500" />
            Organization Tenants
          </CardTitle>
          <CardDescription>
            View AI status and budget usage for each tenant
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {tenants.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No tenants found in this organization
              </div>
            ) : (
              tenants.map((tenant) => (
                <div
                  key={tenant.tenant_id}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors cursor-pointer"
                  onClick={() => openTenantDetails(tenant)}
                  data-testid={`tenant-row-${tenant.tenant_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{tenant.tenant_name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{tenant.active_agents}/{tenant.total_agents} agents</span>
                        <span>•</span>
                        <span>{tenant.plan_name}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {tenant.is_demo && (
                      <Badge variant="outline" className="border-purple-500/50 text-purple-600">
                        Demo
                      </Badge>
                    )}
                    {tenant.kill_switch_active ? (
                      <Badge variant="destructive" className="flex items-center gap-1">
                        <XCircle className="h-3 w-3" />
                        Kill Switch
                      </Badge>
                    ) : tenant.ai_enabled ? (
                      <Badge variant="outline" className="border-green-500/50 text-green-600 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        AI Enabled
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-gray-500/50 text-gray-500 flex items-center gap-1">
                        <PauseCircle className="h-3 w-3" />
                        AI Disabled
                      </Badge>
                    )}
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tenant Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-500" />
              {selectedTenant?.tenant_name}
            </DialogTitle>
            <DialogDescription>
              Detailed AI status and budget information (read-only)
            </DialogDescription>
          </DialogHeader>

          {detailsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-500">Loading details...</span>
            </div>
          ) : tenantDetails ? (
            <div className="space-y-6">
              {/* AI Status Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Bot className="h-4 w-4 text-blue-500" />
                  AI Status
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-xs text-gray-500">AI Enabled</p>
                    <p className={cn(
                      "font-semibold",
                      tenantDetails.aiStatus.ai_enabled ? "text-green-600" : "text-gray-500"
                    )}>
                      {tenantDetails.aiStatus.ai_enabled ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-xs text-gray-500">Kill Switch</p>
                    <p className={cn(
                      "font-semibold",
                      tenantDetails.aiStatus.kill_switch_active ? "text-red-600" : "text-green-600"
                    )}>
                      {tenantDetails.aiStatus.kill_switch_active ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-xs text-gray-500">Execution Mode</p>
                    <p className="font-semibold">{tenantDetails.aiStatus.execution_mode}</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-xs text-gray-500">Runs (30 days)</p>
                    <p className="font-semibold">{tenantDetails.aiStatus.total_runs_30d}</p>
                  </div>
                </div>

                {/* Agents */}
                {tenantDetails.aiStatus.agents?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-2">Agent Status</p>
                    <div className="flex flex-wrap gap-2">
                      {tenantDetails.aiStatus.agents.map((agent) => (
                        <Badge
                          key={agent.type}
                          variant="outline"
                          className={cn(
                            agent.enabled && !agent.paused
                              ? "border-green-500/50 text-green-600"
                              : "border-gray-500/50 text-gray-500"
                          )}
                        >
                          {agent.type}
                          {agent.enabled && !agent.paused ? (
                            <Power className="h-3 w-3 ml-1" />
                          ) : (
                            <PauseCircle className="h-3 w-3 ml-1" />
                          )}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Budget Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-green-500" />
                  Budget Usage
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Plan</span>
                    <span className="font-semibold">{tenantDetails.budget.plan_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Monthly Budget</span>
                    <span className="font-semibold">${tenantDetails.budget.monthly_budget_usd?.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Used</span>
                    <span className="font-semibold text-blue-600">${tenantDetails.budget.used_budget_usd?.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Remaining</span>
                    <span className="font-semibold text-green-600">${tenantDetails.budget.remaining_budget_usd?.toFixed(2)}</span>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-500">Utilization</span>
                      <span className={cn(
                        "text-xs font-semibold",
                        tenantDetails.budget.hard_limit_reached
                          ? "text-red-600"
                          : tenantDetails.budget.soft_limit_reached
                            ? "text-amber-600"
                            : "text-green-600"
                      )}>
                        {tenantDetails.budget.budget_utilization_pct?.toFixed(1)}%
                      </span>
                    </div>
                    <Progress 
                      value={Math.min(tenantDetails.budget.budget_utilization_pct || 0, 100)} 
                      className={cn(
                        "h-2",
                        tenantDetails.budget.hard_limit_reached && "[&>div]:bg-red-500",
                        tenantDetails.budget.soft_limit_reached && !tenantDetails.budget.hard_limit_reached && "[&>div]:bg-amber-500"
                      )}
                    />
                  </div>
                  {tenantDetails.budget.soft_limit_reached && (
                    <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-900/20">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-700 dark:text-amber-300 text-xs">
                        {tenantDetails.budget.hard_limit_reached 
                          ? 'Hard limit reached - AI execution blocked'
                          : 'Soft limit reached - warning sent'}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </div>

              {/* Read-Only Notice */}
              <Alert className="border-blue-500/50 bg-blue-50 dark:bg-blue-900/20">
                <Eye className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-700 dark:text-blue-300 text-xs">
                  This is view-only. To modify AI settings or budget, contact the tenant admin directly.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No details available
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <div className="text-center text-gray-500 text-sm">
        <p>Last updated: {lastRefresh.toLocaleString()}</p>
        <p className="mt-1">Organization visibility is read-only. Tenant modifications require tenant admin access.</p>
      </div>
    </div>
  );
}
